<script setup lang="ts">
/**
 * /templates — the site's page for "resume templates".
 *
 * It used to be the homepage gallery under a title band and nothing else, which
 * answered no question: the head query is informational, and the visitor wants
 * to know which format to use before they care which of three layouts is
 * prettier. So the order is answer, then gallery, then the neighbouring
 * questions (Word/Docs/Canva, free vs paid, cover letter, first resume, web
 * business card), each ending in the internal link that matches its own
 * subject.
 *
 * Deliberately one page rather than a second one: a separate /resume-templates
 * would compete with this URL for the same query.
 */
const { t } = useI18n()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/sablony-zivotopisu', de: '/lebenslauf-vorlagen', pl: '/szablony-cv' } })

/** Answered in prose on the page, and mirrored into FAQPage structured data. */
const FAQ_IDS = ['format', 'coverLetter', 'best', 'free', 'ats', 'change'] as const

const faqItems = computed(() =>
  FAQ_IDS.map(id => ({
    id,
    question: t(`templatesPage.faq.${id}.q`),
    answer: t(`templatesPage.faq.${id}.a`),
  })),
)

const extraNodes = computed(() => [faqPageNode('/templates', faqItems.value)])
</script>

<template>
  <MarketingFeaturePage
    seo-key="templates"
    path="/templates"
    :breadcrumb-title="t('templatesPage.heading')"
    :extra-nodes="extraNodes"
  >
    <!-- Answer before product: format decision and a worked example -->
    <MarketingTemplatesIntro />

    <!-- The form filling the preview, then every template in turn -->
    <MarketingFeatureVideo video="templates" />

    <!-- The showcase paints its own band and carries the #templates anchor -->
    <LazyHomepageTemplateShowcase />

    <MarketingTemplatesGuide />

    <MarketingFaqSection
      :title="t('templatesPage.faq.title')"
      :items="faqItems"
      name="templates-faq"
    />
  </MarketingFeaturePage>
</template>
